export type UserFacingErrorContext =
  | 'login'
  | 'tokenLogin'
  | 'passkeySignIn'
  | 'passkeyRegistration'
  | 'signup'
  | 'resetPassword'
  | 'sendCode'
  | 'adminInvite'
  | 'adminInviteAccept'
  | 'nonjointInvite'
  | 'verifyEmail'
  | 'users';

const DEFAULT_MESSAGES: Record<UserFacingErrorContext, string> = {
  login: 'Invalid email or password.',
  tokenLogin: 'We could not complete sign-in. Please try again.',
  passkeySignIn: 'Passkey sign-in failed. Try your password.',
  passkeyRegistration: 'Failed to add passkey.',
  signup: 'Something went wrong. Please try again or contact Central Bank customer service.',
  resetPassword: 'Unable to reset password.',
  sendCode: 'Failed to send code.',
  adminInvite: 'Failed to send invitation.',
  adminInviteAccept: 'Failed to accept invitation.',
  nonjointInvite: 'Invalid or expired invitation link.',
  verifyEmail: 'Failed to verify email.',
  users: 'Failed to load users.',
};

const TEMPORARY_DIFFICULTIES_MESSAGE = 'We are experiencing temporary difficulties. Please contact Central Bank for assistance.';

function getStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

export function getErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const body = (error as { error?: unknown }).error;
  if (!body || typeof body !== 'object') return undefined;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

export function isEmailNotVerifiedError(error: unknown): boolean {
  return getErrorCode(error) === 'EMAIL_NOT_VERIFIED';
}

export function userFacingErrorMessage(error: unknown, context: UserFacingErrorContext): string {
  if (isEmailNotVerifiedError(error)) {
    return 'Please verify your email before signing in.';
  }

  if ((context === 'login' || context === 'tokenLogin') && (getStatus(error) ?? 0) >= 500) {
    return TEMPORARY_DIFFICULTIES_MESSAGE;
  }

  return DEFAULT_MESSAGES[context];
}
